/**
 * Evidence age disclosure.
 *
 * THE RULE
 * --------
 * Age never blocks. If an approved artifact is the newest one we have, we use
 * it however old it is, and we say what date it is from. "This is what you gave
 * me, and this is what I used" is the product — not a withheld answer.
 *
 * WHAT THIS REPLACED
 * ------------------
 * Freshness used to be an admission test. `esg-emissions.data-lake-source.ts`
 * dropped any `shipment_tonne_km_snapshot` older than seven days, so a fully
 * APPROVED, connected, ingest-backed 14-day-old snapshot read as *absent* — and
 * the answer told the user the data "ha[s] not been loaded or connected yet"
 * (sweep row 14, 2026-07-24). That is a false claim about our own state,
 * produced by an architecture whose entire job is to not make false claims.
 *
 * A seven-day window on annual filings is meaningless anyway: a 10-K is filed
 * once a year, so every governed finance figure is "stale" for 51 weeks out of
 * 52. We gated the harmless thing.
 *
 * WHAT AGE IS *NOT*
 * -----------------
 * Age is not the risk. Period incoherence is: summing FY2025 finance with
 * FY2023 carrier volumes is fiction no matter how fresh either input is. That
 * check belongs where numbers COMBINE, not where a single artifact is read. It
 * is deliberately not implemented here.
 *
 * THE SAFETY PROPERTY
 * -------------------
 * There is exactly one, and it is the whole reason this module exists: **a
 * number rendered without its as-of is a fabrication.** Everything below exists
 * so the date travels with the figure all the way to the published payload.
 */

import { EVIDENCE_STALE_BEHAVIORS } from './governed-capability-manifest';

/**
 * Retained because `EvidencePolicy.staleBehavior` is still declared across the
 * module manifests. It is now ADVISORY: no layer reads it to decide admission.
 * Age never blocks, so `'block'` no longer means anything at runtime.
 */
export type EvidenceStaleBehavior = (typeof EVIDENCE_STALE_BEHAVIORS)[number];

/**
 * The as-of disclosure that travels with a governed artifact to the published
 * payload. Every field is derived from the artifact — none of it is authored
 * prose that could drift from the data it describes.
 */
export type EvidenceAgeDisclosure = {
  /** The manifest evidence key this artifact satisfies. */
  evidenceKey: string;
  /** Human label for the key, e.g. "Shipment tonne-km snapshot". */
  evidenceLabel: string;
  /**
   * The artifact's own as-of instant, ISO-8601. `null` only when the source
   * system reported no readable date — in which case the disclosure says
   * exactly that, rather than letting the figure read as current.
   */
  asOf: string | null;
  /** Display form of `asOf`, e.g. "10 Jul 2026". Never a relative phrase. */
  asOfLabel: string | null;
  /** Whole hours between `asOf` and the evaluation instant; null when undated. */
  ageHours: number | null;
  /**
   * The declared refresh cadence in hours, when the manifest states one.
   * ADVISORY ONLY — a ranking and diagnostics signal. It gates nothing.
   */
  refreshWindowHours: number | null;
  /** True when `ageHours` exceeds `refreshWindowHours`. Blocks nothing. */
  pastRefreshWindow: boolean;
  /**
   * The sentence a reader sees. Always present, whether or not the artifact is
   * past its window — rule 2 is that every number carries its as-of, always.
   */
  label: string;
};

const MS_PER_HOUR = 3_600_000;

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "2026-07-10T12:00:00.000Z" -> "10 Jul 2026". Returns null when unparseable. */
export function formatEvidenceAsOfLabel(value: unknown): string | null {
  const instant = parseInstant(value);
  if (instant === null) return null;
  const date = new Date(instant);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export type EvidenceAgeInput = {
  evidenceKey: string;
  /** Optional friendly name; falls back to a humanised `evidenceKey`. */
  evidenceLabel?: string | null;
  /** The artifact's as-of instant. */
  asOf: unknown;
  /** Evaluation instant. Defaults to now. */
  now?: unknown;
  /** Declared refresh cadence. Advisory; supply either form. */
  refreshWindowHours?: number | null;
  refreshWindowMs?: number | null;
};

/**
 * Describe how old an artifact is. Always succeeds, never refuses — by the time
 * this is called the caller has already chosen this artifact because it is the
 * newest approved one available.
 */
export function discloseEvidenceAge(input: EvidenceAgeInput): EvidenceAgeDisclosure {
  const refreshWindowHours = resolveRefreshWindowHours(input);
  const asOf = parseInstant(input.asOf);
  const now = input.now === undefined ? Date.now() : parseInstant(input.now);
  const asOfIso = asOf === null ? null : new Date(asOf).toISOString();
  const asOfLabel = asOfIso === null ? null : formatEvidenceAsOfLabel(asOfIso);
  const ageHours =
    asOf !== null && now !== null ? Math.floor((now - asOf) / MS_PER_HOUR) : null;
  const pastRefreshWindow =
    ageHours !== null && refreshWindowHours !== null && ageHours > refreshWindowHours;
  const evidenceLabel =
    normalizeLabel(input.evidenceLabel) ?? humanizeEvidenceKey(input.evidenceKey);

  return {
    evidenceKey: input.evidenceKey,
    evidenceLabel,
    asOf: asOfIso,
    asOfLabel,
    ageHours,
    refreshWindowHours,
    pastRefreshWindow,
    label: buildAsOfSentence(evidenceLabel, asOfLabel),
  };
}

/**
 * The reader-facing sentence.
 *
 * It states the date and nothing else. No "stale", no "exceeded its window", no
 * apology — those are internal concepts that mean nothing to a CFO and imply
 * the figure is defective when it is simply the most recent approved data. Age
 * in hours and declared cadence stay on the structured disclosure for ranking
 * and diagnostics.
 */
export function buildAsOfSentence(
  evidenceLabel: string,
  asOfLabel: string | null,
): string {
  return asOfLabel === null
    ? `${evidenceLabel}: the source reported no as-of date.`
    : `${evidenceLabel} as of ${asOfLabel}.`;
}

/** Dense chip form for tight surfaces: "as of 10 Jul 2026". */
export function evidenceAsOfChipText(disclosure: EvidenceAgeDisclosure): string {
  return disclosure.asOfLabel === null
    ? 'as-of date not reported'
    : `as of ${disclosure.asOfLabel}`;
}

/**
 * Rule 4: freshness is a RANKING signal, not a gate. Given candidate artifacts
 * for one evidence key, prefer the newest as-of; undated candidates sort last.
 * With a single candidate this returns it unchanged, however old it is.
 */
export function preferNewestByAsOf<T>(
  candidates: readonly T[],
  asOfOf: (candidate: T) => unknown,
): T[] {
  return [...candidates].sort((left, right) => {
    const leftAt = parseInstant(asOfOf(left));
    const rightAt = parseInstant(asOfOf(right));
    if (leftAt === rightAt) return 0;
    if (leftAt === null) return 1;
    if (rightAt === null) return -1;
    return rightAt - leftAt;
  });
}

function resolveRefreshWindowHours(input: EvidenceAgeInput): number | null {
  const { refreshWindowHours, refreshWindowMs } = input;
  if (
    typeof refreshWindowHours === 'number' &&
    Number.isFinite(refreshWindowHours) &&
    refreshWindowHours > 0
  ) {
    return refreshWindowHours;
  }
  if (
    typeof refreshWindowMs === 'number' &&
    Number.isFinite(refreshWindowMs) &&
    refreshWindowMs > 0
  ) {
    return refreshWindowMs / MS_PER_HOUR;
  }
  return null;
}

function parseInstant(value: unknown): number | null {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.getTime() : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeLabel(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Bare regex title-case with NO vocabulary filter — never guaranteed safe for
 * an executive reader. #5460 P1-1 (ChatGPT exact-head HOLD, 2026-08-29): this
 * must never be the ACCEPTED fallback for a key a production
 * `GovernedCapabilityManifest` declares — every such key is required to have
 * a declared label (the governed metric registry, or
 * `EVIDENCE_REQUIREMENT_READER_LABELS`), enforced by
 * `evidence-requirement-labels.repo-coverage.spec.ts` in apps/api, which
 * proves this function is unreachable from `evidenceRequirementReaderLabel`
 * for every key currently declared in production. It stays exported as a
 * last-resort safety net (never a stripped/sanitized one — masking banned
 * words at runtime is explicitly not an acceptable substitute for a declared
 * label) for a genuinely undeclared key, and for non-production/debug
 * callers that have no manifest-declared label to resolve at all.
 */
export function humanizeEvidenceKey(evidenceKey: string): string {
  const words = String(evidenceKey ?? '')
    .replace(/[_\-.:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words) return 'Approved source';
  return words.charAt(0).toUpperCase() + words.slice(1);
}
